/*
 * Bharat Basket — shared quick-cart control for product cards.
 *
 * Renders an "add to cart" icon that turns into a +/- quantity stepper once the
 * variant is in the cart. Used on the homepage featured grid and on the standard
 * collection / recommendation / search product cards so they all behave the same.
 *
 * Plain custom element (no ES module imports) so it can be loaded with a normal
 * deferred <script> tag and still auto-wire cards added later by infinite scroll.
 *
 * Speed: the click updates the stepper, bumps the header cart bubble and launches
 * the theme's <fly-to-cart> animation *immediately* (optimistic UI). The network
 * request to Shopify runs in the background and the authoritative cart total is
 * reconciled once it returns — so the control never feels like it's waiting.
 *
 * The header cart bubble is kept in sync by dispatching the theme's own
 * `cart:update` event (cart-icon.js listens for it):
 *   - optimistic bump  -> source 'product-form-component' (cart-icon ADDS the delta)
 *   - authoritative set -> source 'bb-quick-cart'          (cart-icon SETS the total)
 */
(function () {
  if (window.__bbQuickCartInit) return;
  window.__bbQuickCartInit = true;

  /** @type {Set<BBQuickCart>} */
  var instances = new Set();
  var lastCart = null;
  var inflight = false;
  var refetchQueued = false;

  function refetch() {
    if (inflight) {
      refetchQueued = true;
      return;
    }
    inflight = true;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { lastCart = cart; applyAll(); })
      .catch(function () {})
      .then(function () {
        inflight = false;
        if (refetchQueued) { refetchQueued = false; refetch(); }
      });
  }

  function countFor(variantId) {
    if (!lastCart || !lastCart.items) return 0;
    var n = 0;
    for (var i = 0; i < lastCart.items.length; i++) {
      if (String(lastCart.items[i].variant_id) === String(variantId)) {
        n += lastCart.items[i].quantity;
      }
    }
    return n;
  }

  // Reconcile every idle card with the authoritative cart. Cards mid-change keep
  // their optimistic value — their own request will reconcile them when it lands.
  function applyAll() {
    instances.forEach(function (el) {
      if (el.busy) return;
      el.syncFromCart(countFor(el.variantId));
    });
  }

  // Keep cards in sync with cart changes that happen elsewhere (product page add,
  // cart page edits, etc.). Ignore the events we dispatch ourselves.
  document.addEventListener('cart:update', function (event) {
    var data = event && event.detail && event.detail.data;
    if (!data) { refetch(); return; }
    if (data.source === 'bb-quick-cart') return; // our authoritative set — already applied
    if (data.bbOptimistic) return;               // our optimistic bubble bump — ignore
    refetch();
  });

  // Tell cart-icon.js to ADD `delta` to the bubble right now (no network wait).
  function bumpBubble(delta) {
    if (!delta) return;
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { itemCount: delta, source: 'product-form-component', bbOptimistic: true } },
      })
    );
  }

  // Tell cart-icon.js to SET the bubble to the real cart total.
  function setBubble(itemCount) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { itemCount: itemCount, source: 'bb-quick-cart' } },
      })
    );
  }

  // Launch the theme's native fly-to-cart animation: a chip of the product image
  // arcs from the card to the header cart icon. <fly-to-cart> is registered by
  // fly-to-cart.js (loaded globally in scripts.liquid) and removes itself when done.
  function flyToCart(sourceEl, imageUrl) {
    if (!imageUrl || !sourceEl) return;
    var cartIcon = document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !customElements.get('fly-to-cart')) return;

    var fly = document.createElement('fly-to-cart');
    fly.classList.add('fly-to-cart--quick');
    fly.style.setProperty('background-image', 'url(' + imageUrl + ')');
    fly.style.setProperty('--start-opacity', '0');
    fly.source = sourceEl;
    fly.destination = cartIcon;
    document.body.appendChild(fly);
  }

  class BBQuickCart extends HTMLElement {
    connectedCallback() {
      this.variantId = this.getAttribute('data-variant');
      this.image = this.getAttribute('data-image');
      this.atc = this.querySelector('.bb-atc');
      this.qtyBox = this.querySelector('.bb-qty');
      if (!this.variantId || !this.atc || !this.qtyBox) return;
      this.numEl = this.qtyBox.querySelector('.bb-qty__num');
      this.incBtn = this.qtyBox.querySelector('[data-act="inc"]');
      this.decBtn = this.qtyBox.querySelector('[data-act="dec"]');
      this.qty = 0;
      this.busy = false;
      this.pendingTarget = null;

      this.atc.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.setQty(1); });
      if (this.incBtn) this.incBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.setQty(this.qty + 1); });
      if (this.decBtn) this.decBtn.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); this.setQty(this.qty - 1); });

      instances.add(this);
      if (lastCart) this.syncFromCart(countFor(this.variantId));
      else refetch();
    }

    disconnectedCallback() {
      instances.delete(this);
    }

    render() {
      if (this.qty > 0) {
        this.atc.hidden = true;
        this.qtyBox.hidden = false;
        this.numEl.textContent = this.qty;
      } else {
        this.atc.hidden = false;
        this.qtyBox.hidden = true;
      }
    }

    syncFromCart(n) {
      this.qty = n;
      this.render();
    }

    // Optimistically move to quantity `n`, then persist in the background.
    setQty(n) {
      n = Math.max(0, n);
      var prev = this.qty;
      if (n === prev) return;
      var delta = n - prev;
      var added = delta > 0;

      // 1) Instant UI — stepper, pop, fly animation and bubble all update now.
      this.qty = n;
      this.render();
      if (added) {
        this.pulse();
        flyToCart(this, this.image);
      }
      bumpBubble(delta);

      // 2) Persist to Shopify in the background.
      this.commit();
    }

    commit() {
      if (this.busy) return; // a request is in flight; it re-checks the target when done
      this.busy = true;
      const target = this.qty;
      const updates = {};
      updates[this.variantId] = target;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates: updates }),
      })
        .then((r) => r.json())
        .then((cart) => {
          lastCart = cart;
          const count = cart && typeof cart.item_count !== 'undefined' ? cart.item_count : 0;
          setBubble(count);
          applyAll(); // sync other idle cards (this one is still busy, so untouched)
        })
        .catch(() => {
          // Network failed — drop the optimistic state back to the real cart.
          refetch();
        })
        .then(() => {
          this.busy = false;
          // The shopper changed quantity while we were saving — persist the latest.
          if (this.qty !== target) this.commit();
          else this.syncFromCart(countFor(this.variantId));
        });
    }

    pulse() {
      const target = this.qty > 0 ? this.qtyBox : this.atc;
      target.classList.remove('bb-cart--pop');
      // force reflow so the animation can replay on repeated adds
      void target.offsetWidth;
      target.classList.add('bb-cart--pop');
    }
  }

  if (!customElements.get('bb-quick-cart')) {
    customElements.define('bb-quick-cart', BBQuickCart);
  }
})();
